import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, HelpCircle, Maximize2, Pause, Play, RotateCcw, Volume2, VolumeX, X } from 'lucide-react';
import ErrorBoundary from './ErrorBoundary';
import GameArtwork from './GameArtwork';
import { arcadeAudio } from '../platform/audio';
import type { GameDefinition, GameResult } from '../platform/types';

interface GamePlayerProps {
  game: GameDefinition;
  muted: boolean;
  onMutedChange: (muted: boolean) => void;
  onExit: () => void;
  onResult: (result: GameResult) => void;
}

export default function GamePlayer({ game, muted, onMutedChange, onExit, onResult }: GamePlayerProps) {
  const [paused, setPaused] = useState(false);
  const [help, setHelp] = useState(false);
  const [restartToken, setRestartToken] = useState(0);
  const [score, setScore] = useState(0);
  const [result, setResult] = useState<GameResult | null>(null);
  const cabinetRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const Game = useMemo(() => lazy(game.component), [game]);
  const canFullscreen = typeof document !== 'undefined' && document.fullscreenEnabled;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.fullscreenElement) return;
      if (result) return;
      if (help) {
        setHelp(false);
        return;
      }
      setPaused((value) => !value);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [help, result]);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusable = [...modal.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')];
    focusable[0]?.focus();
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || focusable.length === 0) return;
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    modal.addEventListener('keydown', trapFocus);
    return () => {
      modal.removeEventListener('keydown', trapFocus);
      previousFocusRef.current?.focus();
    };
  }, [help, paused, result]);

  const restart = () => {
    arcadeAudio.cue('click');
    setResult(null);
    setHelp(false);
    setPaused(false);
    setScore(0);
    setRestartToken((value) => value + 1);
  };

  const complete = useCallback((next: GameResult) => {
    setResult(next);
    setPaused(true);
    onResult(next);
    arcadeAudio.cue(next.won ? 'win' : 'lose');
  }, [onResult]);

  const toggleMute = () => {
    const next = !muted;
    arcadeAudio.setMuted(next);
    onMutedChange(next);
  };

  const fullscreen = async () => {
    try {
      if (!document.fullscreenElement && cabinetRef.current?.requestFullscreen) await cabinetRef.current.requestFullscreen();
      else if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
    } catch {
      // Fullscreen can be denied by browser permissions without affecting play.
    }
  };

  return (
    <main className="player" style={{ '--game-ink': game.palette.ink, '--game-field': game.palette.field, '--game-accent': game.palette.accent, '--game-spark': game.palette.spark } as React.CSSProperties}>
      <header className="player__bar">
        <button className="icon-button icon-button--back" onClick={onExit}><ArrowLeft size={20} /><span>Arcade</span></button>
        <div className="player__identity"><span>{game.genre}</span><strong>{game.title}</strong></div>
        <div className="player__score"><small>Score</small><strong>{Math.round(score).toLocaleString()}</strong></div>
        <div className="player__tools">
          <button className="icon-button" onClick={() => { setPaused(true); setHelp(true); }} aria-label="Show controls"><HelpCircle size={19} /></button>
          <button className="icon-button" disabled={help || Boolean(result)} onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>{muted ? <VolumeX size={19} /> : <Volume2 size={19} />}</button>
          <button className="icon-button" disabled={help || Boolean(result)} onClick={() => setPaused((value) => !value)} aria-label={paused ? 'Resume' : 'Pause'}>{paused ? <Play size={19} /> : <Pause size={19} />}</button>
          <button className="icon-button" disabled={help || Boolean(result)} onClick={restart} aria-label="Restart"><RotateCcw size={19} /></button>
          {canFullscreen && <button className="icon-button player__fullscreen" disabled={help || Boolean(result)} onClick={fullscreen} aria-label="Fullscreen"><Maximize2 size={19} /></button>}
        </div>
      </header>

      <div className="cabinet" ref={cabinetRef} data-game-id={game.id}>
        <div className="cabinet__screen">
          <ErrorBoundary onExit={onExit}>
            <Suspense fallback={<div className="game-loading"><GameArtwork game={game.id} compact /><span>Warming up the cabinet…</span><i /></div>}>
              <Game paused={paused} muted={muted} restartToken={restartToken} onScore={setScore} onComplete={complete} />
            </Suspense>
          </ErrorBoundary>
          {paused && !result && !help && (
            <div className="pause-sheet" ref={modalRef} role="dialog" aria-modal="true" aria-label="Game paused">
              <span className="eyebrow">Cabinet paused</span>
              <h2>Catch your breath.</h2>
              <div className="pause-sheet__actions">
                <button className="button button--light" onClick={() => setPaused(false)}><Play fill="currentColor" size={18} /> Resume</button>
                <button className="button button--ghost-light" onClick={restart}><RotateCcw size={18} /> Restart</button>
                <button className="button button--ghost-light" onClick={onExit}><ArrowLeft size={18} /> Exit</button>
              </div>
            </div>
          )}
          {help && (
            <div className="help-sheet" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="controls-heading">
              <button className="help-sheet__close" onClick={() => setHelp(false)} aria-label="Close controls"><X size={20} /></button>
              <span className="eyebrow">How to play</span>
              <h2 id="controls-heading">{game.kicker}</h2>
              <p>{game.description}</p>
              <div className="control-list">{game.controls.map((control, index) => <span key={control}><b>0{index + 1}</b>{control}</span>)}</div>
              <button className="button button--light" onClick={() => { setHelp(false); setPaused(false); }}><Play fill="currentColor" size={18} /> Back to game</button>
            </div>
          )}
          {result && (
            <div className="result-sheet" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="result-heading">
              <span className="result-sheet__seal">{result.won ? 'CLEAR!' : 'RUN OVER'}</span>
              <span className="eyebrow">{game.title}</span>
              <h2 id="result-heading">{result.summary}</h2>
              <div className="result-sheet__score"><small>Final score</small><strong>{Math.round(result.score).toLocaleString()}</strong></div>
              {result.stamps?.length ? <p className="result-sheet__stamps">New stamp: {result.stamps.join(' · ')}</p> : null}
              <div className="result-sheet__actions">
                <button className="button button--light" onClick={restart}><RotateCcw size={18} /> Play again</button>
                <button className="button button--ghost-light" onClick={onExit}>Choose a game</button>
              </div>
            </div>
          )}
        </div>
        <footer className="cabinet__footer">
          <span>{game.kicker}</span>
          <div><kbd>ESC</kbd> pause <kbd>F</kbd> full screen</div>
        </footer>
      </div>
    </main>
  );
}
